<template>
  <Toast position="center"/>
  <div v-if="loading" class="bg-gray-50">
    <h4>Please wait...</h4>
  </div>
  <div v-else-if="mode === 'password-reset'">
    <div class="sticky top-0 left-0 flex h-screen">
      <div class="flex-row flex-1 overflow-auto border-2">
        <div class="flex overflow-auto primary-content main-content-container password-reset">
          <main class="flex-auto">
            <section aria-labelledby="primary-heading" class="h-full">
              <div class="w-full px-5 py-1">
                <PasswordReset :passwordResetObject="passwordResetObject"/>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="mode === 'authenticate'">
    <div class="sticky top-0 left-0 flex h-screen">
      <div class="flex-row flex-1 overflow-auto border-2">
        <div class="flex overflow-auto primary-content main-content-container login">
          <main class="flex-auto">
            <section aria-labelledby="primary-heading" class="h-full">
              <div class="w-full px-5 py-1">
                <UserLogin @onAuthenticated="onAuthenticated"/>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="mode === 'basic'" class="vh-100 bg-gray-50">
    <div class="flex pt-4 app-shell-top-bar sm:px-4 drop-shadow-lg">
      <div class="flex-none w-[205px] logo-container sticky top-0">
        <div class="float-left">
          <RmsLogo :logoColor="colorMode"/>
        </div>
        <div v-if="AppConfigStore.methods.getEnv() !== 'production'" class="float-right bg-[#B91C1C] p-2 h-full">
          <p class="text-center font-bold uppercase">not</p>
          <p class="text-center font-bold uppercase">production</p>
          <p class="text-center text-xs uppercase">{{ AppConfigStore.methods.getEnv() }}</p>
        </div>
      </div>

      <div class="flex-1"></div>

      <div class="ml-4 text-s">
        <Button
          type="button"
          icon="pi pi-user"
          class="user-profile-button"
          :label="UserDetailsStore.methods.getName()"
          aria:haspopup="true"
          aria-controls="overlay_panel"
          @click="toggle"
        />

        <OverlayPanel
          id="overlay_panel"
          ref="op"
          appendTo="body"
          :showCloseIcon="false"
          style="width: 175px"
          :breakpoints="{'960px': '75vw'}"
        >
          <ul class="w-full">
            <li class="w-full">
              <a
                class="inline-block w-full px-4 py-2 text-lg transition-all duration-200 ease-in-out hover:bg-blue-100"
                href="/authentication/logout"
              >Logout</a>
            </li>
          </ul>
        </OverlayPanel>
      </div>
    </div>

    <div class="sticky top-0 left-0 flex h-screen">
      <div
        :class="largeNavOpen ? 'relative left-0 w-[220px] pb-10 overflow-auto' : 'h-full absolute -left-[220px] w-[220px]'"
        class="z-50 transition-all duration-500 ease-in-out sidebar-container"
      >
        <div class="w-full py-4">
          <div class="w-full pt-4 pb-4">
            <SideBarNav
              :navToggle="toggleMenuSize"
              :navItems="navigationStore.methods.getItems()"
              @onClick="onNavClick"
            />
          </div>
        </div>
      </div>
      <div
        :class="!largeNavOpen ? 'relative left-0 w-[40px] overflow-auto pb-10' : 'h-full absolute  -left-[100px]'"
        class="z-50 transition-all duration-500 ease-in-out sidebar-container"
      >
        <div class="w-full py-4">
          <div class="w-full pb-4 text-center">
            <SideBarNavSmall
              :navToggle="toggleMenuSize"
              :navItems="navigationStore.methods.getItems()"
              @onClick="toggleMenuSize"
            />
          </div>
        </div>
      </div>

      <Transition>
        <div v-show="largeNavOpen" class="absolute version-container w-[185px] bottom-20 left-4 z-50 text-sm text-gray-300">
          <div>RMS v{{ AppConfigStore.methods.getVersion() }}</div>
          <div>
            {{ UserDetailsStore.methods.getEffectiveTimeZone() }}<br>{{ UserDetailsStore.methods.getEffectiveTimeZoneNamedOffset() }}
          </div>
        </div>
      </Transition>

      <div class="flex-row flex-1 overflow-auto border-2">
        <div class="flex overflow-auto primary-content main-content-container">
          <main class="flex-auto">
            <section aria-labelledby="primary-heading" class="h-full">
              <div class="w-full px-5 py-1">
                <router-view/>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, provide, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import OverlayPanel from 'primevue/overlaypanel'
import RmsLogo from '@/components/design/RmsLogo.vue'
import SideBarNav from '@/components/navigation/SideBarNav.vue'
import SideBarNavSmall from '@/components/navigation/SideBarNavSmall.vue'
import UserLogin from '@/components/auth/UserLogin.vue'
import PasswordReset from '@/components/auth/PasswordReset.vue'
import UsersService from '@/services/accounts/UsersService'
import AccountsService from '@/services/accounts/AccountsService'
import AppService from '@/services/AppService'
import UserDetailsStore from '@/stores/UserDetailsStore'
import NavigationStore from '@/stores/NavigationStore'
import AppConfigStore from '@/stores/AppConfigStore'
import Toast from 'primevue/toast'

export default {
  components: {
    PasswordReset,
    UserLogin,
    SideBarNav,
    SideBarNavSmall,
    Button,
    OverlayPanel,
    Toast,
    RmsLogo,
  },
  setup() {
    const route = useRoute()
    provide('UserDetailsStore', UserDetailsStore)
    const largeNavOpen = ref(true)
    const colorMode = ref('light_sans_company')
    const loading = ref(true)
    const userAuthenticated = ref(false)
    const mode = ref(undefined)
    const passwordResetObject = ref({
      token: '',
      email: '',
    })

    const userService = new UsersService()
    const appService = new AppService()
    const accountService = new AccountsService()
    const navigationStore = NavigationStore
    const op = ref()
    const toast = useToast()

    const toggle = (event) => op.value.toggle(event)
    const onNavClick = (itemMap) => navigationStore.methods.changeCurrentNav(itemMap)
    const toggleMenuSize = () => largeNavOpen.value = !largeNavOpen.value
    const currentRoute = computed(() => useRoute().name)

    const loadData = () => {
      Promise.all([
        appService.config(),
        userService.user(),
        userService.roles(),
      ])
        .then(responses => {
          userAuthenticated.value = true
          mode.value = 'basic'
          handleAppResponse(responses[0])
          const userAccountId = handleUserResponse(responses[1])
          handleRolesResponse(responses[2])
          if (!userAccountId) {
            handleUserAccountNotFound()
            throw new Error('User account not found')
          }
          return userAccountId
        })
        .then(userAccountId => {
          return new Promise((resolve, reject) => {
            accountService.retrieve(userAccountId).then(handleAccountResponse)
              .then(() => resolve(true))
              .catch(err => reject(err))
          })
        })
        .then(() => navigationStore.methods.init(UserDetailsStore))
        .catch(error => {
          if (401 === error.response.status) {
            userAuthenticated.value = false
            switch (route.name) {
              case 'PasswordResetPage':
                if (!!route.query.token && !!route.query.email) {
                  mode.value = 'password-reset'
                  passwordResetObject.value = {
                    token: route.query.token,
                    email: decodeURIComponent(route.query.email),
                  }
                } else {
                  window.location.href = '/login'
                }
                break
              case 'LoginPage':
                mode.value = 'authenticate'
                break
              default:
                window.location.href = '/login'
                break
            }
            return Promise.resolve()
          } else {
            return Promise.reject(error)
          }
        })
        .finally(() => {
          loading.value = false
        })
    }

    const handleAppResponse = (response) => {
      AppConfigStore.methods.setEnv(response.env)
      AppConfigStore.methods.setTimezone(response.timezone)
      AppConfigStore.methods.setURL(response.url)
      AppConfigStore.methods.setName(response.name)
      AppConfigStore.methods.setLocale(response.locale)
      AppConfigStore.methods.setMaintenance(response.maintenance)
      AppConfigStore.methods.setVersion(response.version)
    }

    const handleUserResponse = (response) => {
      UserDetailsStore.methods.setId(response.id)
      UserDetailsStore.methods.setName(response.name)
      UserDetailsStore.methods.setPreferredTimeZone(response.preferred_time_zone?.time_zone)
      return response.account_id
    }

    const handleAccountResponse = (account) => {
      UserDetailsStore.methods.setAccount({
        id: account.id,
        code: account.code,
      })
    }

    const handleRolesResponse = (response) => {
      UserDetailsStore.methods.setRoles(response.map(role => ({
        id: role.id,
        name: role.name,
        // oktaId: role.okta_id,
      })))
    }

    const handleUserAccountNotFound = () => {
      toast.add({
        severity: 'error',
        summary: 'User account not found',
        detail: 'User account not found. Please contact an administrator.',
        life: undefined,
      })
    }

    const onAuthenticated = () => window.location.href = '/'

    onMounted(() => {
      loadData()
    })

    return {
      mode,
      colorMode,
      loading,
      userAuthenticated,
      largeNavOpen,
      navigationStore,
      currentRoute,
      SideBarNav,
      SideBarNavSmall,
      onNavClick,
      AppConfigStore,
      UserDetailsStore,
      op,
      toggle,
      toggleMenuSize,
      onAuthenticated,
      passwordResetObject,
    }
  },
}
</script>

<style lang="scss">
.header-space {
  height: calc(100vh - 76px);
}

.main-content-container {
  height: calc(100vh - 70px)
}

.main-content-container.login,
.main-content-container.password-reset {
  background-color: #ebebed;
  height: 100vh
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

#webpack-dev-server-client-overlay-div,
#webpack-dev-server-client-overlay {
  display: none !important;
}
</style>
